import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

const AuthContext = createContext({});

// Check if mock auth mode is enabled
const isMockAuthMode = () => {
  return import.meta.env?.VITE_MOCK_AUTH_MODE === 'true';
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    // Get initial session - SYNCHRONOUS
    const initSession = async () => {
      try {
        // Check for mock auth mode
        if (isMockAuthMode()) {
          const mockUser = localStorage.getItem('mock_auth_user');
          if (mockUser && mounted) {
            setUser(JSON.parse(mockUser));
            setUserProfile(JSON.parse(mockUser));
          }
          if (mounted) setLoading(false);
          return;
        }

        const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
        
        if (sessionError) throw sessionError;
        
        if (mounted) {
          if (session?.user) {
            await loadUserProfile(session?.user?.id);
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('Session init error:', err);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // CRITICAL: Keep auth state listener SYNCHRONOUS
    // DO NOT use async/await inside onAuthStateChange callback
    // Async calls cause race conditions and deadlocks in supabase-js
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (!mounted) return;

      if (event === 'SIGNED_IN') {
        // Load user profile WITHOUT blocking the callback
        loadUserProfile(session?.user?.id)?.catch(console.error);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
      } else if (event === 'TOKEN_REFRESHED') {
        // Refresh user profile WITHOUT blocking
        if (session?.user) {
          loadUserProfile(session?.user?.id)?.catch(console.error);
        }
      }
    });

    initSession();

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const loadUserProfile = async (userId) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized. Please check your environment variables.');
      }

      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) throw error;

      const profile = {
        id: data?.id,
        email: data?.email,
        fullName: data?.full_name,
        role: data?.role,
        createdAt: data?.created_at
      };

      setUser(profile);
      setUserProfile(profile);
    } catch (err) {
      console.error('Load profile error:', err);
      setError(err?.message);
    }
  };

  const signIn = async (email, password) => {
    try {
      setError(null);
      setLoading(true);

      // Mock auth mode - bypass Supabase
      if (isMockAuthMode()) {
        // Create mock user profile
        const mockUser = {
          id: 'mock-user-' + Date.now(),
          email: email || 'test@arbeitsagentur.de',
          fullName: 'Test Benutzer (Mock)',
          role: 'case_worker',
          createdAt: new Date()?.toISOString()
        };
        
        // Store mock user in localStorage
        localStorage.setItem('mock_auth_user', JSON.stringify(mockUser));
        
        // Set user state
        setUser(mockUser);
        setUserProfile(mockUser);
        
        return { data: { user: mockUser }, error: null };
      }

      // Check if Supabase is properly configured
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase ist nicht konfiguriert. Bitte überprüfen Sie die Umgebungsvariablen VITE_SUPABASE_URL und VITE_SUPABASE_ANON_KEY.');
      }

      // Validate inputs
      if (!email || !password) {
        throw new Error('E-Mail und Passwort sind erforderlich.');
      }

      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Profile will be loaded by onAuthStateChange
      return { data, error: null };
    } catch (err) {
      console.error('Sign in error:', err);
      const errorMessage = err?.message || 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.';
      setError(errorMessage);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, fullName, role = 'case_worker') => {
    try {
      setError(null);
      setLoading(true);

      const { data: authData, error: signUpError } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: role
          }
        }
      });

      if (signUpError) throw signUpError;

      return { data: authData, error: null };
    } catch (err) {
      console.error('Sign up error:', err);
      setError(err?.message);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      
      // Mock auth mode - clear mock user
      if (isMockAuthMode()) {
        localStorage.removeItem('mock_auth_user');
        setUser(null);
        setUserProfile(null);
        return { error: null };
      }
      
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      
      setUser(null);
      return { error: null };
    } catch (err) {
      console.error('Sign out error:', err);
      setError(err?.message);
      return { error: err };
    }
  };

  const updateProfile = async (updates) => {
    try {
      setError(null);
      
      if (!user?.id) throw new Error('No user logged in');

      const { error } = await supabase?.from('user_profiles')?.update({
          full_name: updates?.fullName,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id);

      if (error) throw error;

      await loadUserProfile(user?.id);
      return { error: null };
    } catch (err) {
      console.error('Update profile error:', err);
      setError(err?.message);
      return { error: err };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;